import { LoanTransactionWithDetails } from '@/loan/types/loan-transaction-with-details';
import { LoanFrequency } from '@/loan/enums/loan-frequency';
import moment from 'moment';
import { DateFormat } from '@/util/enums/date-format';
import { FormSelectOption } from '@/core/types/form-select-option';
import { uniqBy } from 'lodash';
import { LoanDetails } from '@/loan/types/loan-details';
import { LoanTransaction } from '@/loan/types/loan-transaction';
import { LoanTransactionTypeId } from '@/loan/enums/loan-transaction-type-id';
import { LoanStatement } from '@/loan/types/loan-statement';
import { LoanRecurringPayment } from '@/loan/types/loan-recurring-payment';
import { LoanAutopayConfirmInput } from '@/loan/types/loan-autopay-confirm-input';
import { LoanRecurringPaymentAvailableDates } from '@/loan/types/loan-recurring-payment-available-dates';
import { LOAN_AUTOPAY_DAYS_BETWEEN_SEMI_MONTHLY_DRAFT_DATES } from '@/loan/constants/loan-days-between-autopay-semi-monthly-draft-dates';
import { LoanLinkedAccount } from '@/loan/types/loan-linked-account';
import { LoanDetailsPeriod } from '@/loan/types/loan-details-period';
import { LoanESignModalState } from '@/loan/types/loan-esign-modal-state';
import { LOAN_FREQUENCY_TEXT_MAP } from '@/loan/constants/loan-frequency-text-map';
import { LOAN_FREQUENCY_MAP } from '@/loan/constants/loan-frequency-map';
import { LOAN_API_PREFIX } from '@/loan/constants/loan-api-prefix';
import { LOAN_TRANSACTION_STATUS_MAP } from '@/loan/constants/loan-transaction-status-map';
import { LOAN_TRANSACTION_TYPE_MAP } from '@/loan/constants/loan-transaction-type-map';
import { LOAN_TRANSACTION_DETAIL_MAP } from '@/loan/constants/loan-transaction-detail-map';
import { LoanTransactionDetailId } from '@/loan/enums/loan-transaction-detail-id';
import { LoanTransactionStatusId } from '@/loan/enums/loan-transaction-status-id';
import { UtilHelper } from '@/util/helper';
import { LoanPaymentStatus } from '@/loan/enums/loan-payment-status';

/**
 * Iterates over an array of transactions and adds the amount.
 * Subtracts the amount from the provided amount and returns the result if > 0.
 * If <= 0 then it returns 0;
 *
 * @param transactions - LoanTransactionWithLoanDetails[]
 * @param amount - string
 *
 * @returns number
 */
export const calculateAmountDueWithTransactions = ({
	transactions,
	amount,
}: {
	transactions: LoanTransactionWithDetails[];
	amount: number;
}): number => {
	const transactionsAmount = transactions.reduce(
		(total, transaction) => total + Number(transaction.amount),
		0,
	);

	const amountDifference = Number(amount) - transactionsAmount;

	return amountDifference < 0
		? 0
		: amountDifference;
};

export const calculateAutopayAmountByFrequency = ({
	frequency,
	autopayAmount,
}: {
	frequency: LoanFrequency;
	autopayAmount: number;
}): number => {
	if (frequency === LoanFrequency.MONTHLY) {
		return autopayAmount;
	}

	const divisor = (frequency === LoanFrequency.WEEKLY) ? 4 : 2;
	const paymentByFequency = Math.ceil((autopayAmount / divisor) * 100) / 100;

	return paymentByFequency;
};

/**
 * Given LoanDetails
 * Checks if the loan is past due by comparing the status and deliquentDays
 *
 * @param loanDetails
 * @returns boolean
 */
export const checkIsPastDue = (loanDetails: LoanDetails | null): boolean => {
	const isClosed = loanDetails?.general.status === 'closed';
	const isDelinquent = Number(loanDetails?.general.delinquentDays) > 0;

	return !isClosed && isDelinquent;
};

export const checkIsTransactionOfType = ({
	transaction,
	type,
}: {
	transaction: LoanTransaction;
	type: LoanTransactionTypeId;
}): boolean => transaction.transactionType === type;

/**
 * Filters an array of LoanStatements by the last N months
 *
 * @remarks
 *
 * Given an array of LoanStatements and a number of months,
 * if a statement date is less than or equal to the number of months from a specified date
 * then that statement will be included in the returned array
 *
 * @param statements - array of LoanStatements
 * @param nMonths - number of months
 * @param fromDate - date to filter from, defaults to current date
 *
 * @returns the filtered array of LoanStatements
 */
export const filterStatementsByLastNMonths = (
	statements: LoanStatement[],
	nMonths: number,
	fromDate = new Date(),
): LoanStatement[] => {
	let monthsBetween: number;

	return statements.filter((statement) => {
		monthsBetween = UtilHelper.getTotalMonthsBetweenDates(fromDate, UtilHelper.convertISOStringToDate(statement.date));

		return monthsBetween <= nMonths;
	});
};

/**
 * Filters an array of LoanStatements by a specified year
 *
 * @param statements - array of LoanStatements
 * @param year - string of the year to filter by
 *
 * @returns the filtered array of LoanStatements
 */
export const filterStatementsByYear = (
	statements: LoanStatement[],
	year: string,
): LoanStatement[] => {
	let statementYear: string;

	return statements.filter((statement) => {
		[statementYear] = statement.date.split('-');
		return statementYear === year;
	});
};

export const BI_WEEKLY_PAYMENT_AMOUNT_TEXT = (date: string): string => `Bi-weekly Payments Starting on ${date}`;

export const MONTHLY_PAYMENT_AMOUNT_TEXT = (date: string): string => `Monthly Payment Starting on ${date}`;

export const SEMI_MONTHLY_PAYMENT_AMOUNT_TEXT = (date: string): string => `Semi-monthly (Twice a month) Payments Starting on ${date}`;

export const WEEKLY_PAYMENT_AMOUNT_TEXT = (date: string): string => `Weekly Payments Starting on ${date}`;

/**
 * Gets the Autopay Frequency Text to display
 * based on frequecy value and a draft date
 *
 * @param draftDate - Draft Date
 * @param frequency - Autopay Frequency
 *
 * @returns string value
 */
export const getAutopayPaymentFrequencyFullText = ({
	draftDate,
	frequency,
}: {
	draftDate: Date;
	frequency: LoanFrequency;
}): string => {
	if (frequency === LoanFrequency.BI_WEEKLY) {
		return BI_WEEKLY_PAYMENT_AMOUNT_TEXT(UtilHelper.formatDateForDisplay(draftDate));
	}

	if (frequency === LoanFrequency.MONTHLY) {
		return MONTHLY_PAYMENT_AMOUNT_TEXT(UtilHelper.formatDateForDisplay(draftDate));
	}

	if (frequency === LoanFrequency.SEMI_MONTHLY) {
		return SEMI_MONTHLY_PAYMENT_AMOUNT_TEXT(UtilHelper.formatDateForDisplay(draftDate));
	}

	if (frequency === LoanFrequency.WEEKLY) {
		return WEEKLY_PAYMENT_AMOUNT_TEXT(UtilHelper.formatDateForDisplay(draftDate));
	}

	return '';
};

/**
 * Generates the string value of a single Autopay selection
 * for the Autopay Confirm Modal
 *
 * @param amount - payment amount
 * @param draftDateOne - Draft Date One
 * @param draftDateTwo - Draft Date Two
 * @param frequency - Autopay Frequency
 *
 * @returns string value
 */
export const getAutopayPaymentFrequencyText = ({
	amount,
	draftDateOne,
	draftDateTwo,
	frequency,
}: {
	amount: number;
	draftDateOne: Date;
	draftDateTwo: Date | null;
	frequency: LoanFrequency;
}): string => {
	const dollarAmount = UtilHelper.formatDollar(amount);

	if (frequency === LoanFrequency.WEEKLY) {
		return `${dollarAmount} Every ${moment(draftDateOne).format('dddd')}`;
	}

	if (frequency === LoanFrequency.BI_WEEKLY) {
		return `${dollarAmount} Every other ${moment(draftDateOne).format('dddd')}`;
	}

	if (frequency === LoanFrequency.SEMI_MONTHLY) {
		if (!draftDateTwo) {
			throw new Error(`DraftDateTwo must be defined if Frequency is ${LoanFrequency.SEMI_MONTHLY}`);
		}

		const [firstDate, secondDate] = UtilHelper.sortDatesByDayOfMonth([draftDateOne, draftDateTwo]);

		return `${dollarAmount} Every ${UtilHelper.ordinal(firstDate.getDate())} and ${UtilHelper.ordinal(secondDate.getDate())}`;
	}

	if (frequency === LoanFrequency.MONTHLY) {
		return `${dollarAmount} Monthly`;
	}

	return '';
};

/**
 * Generates the context of the Autopay Confirm Modal
 *
 * @param amount - payment amount
 * @param draftDateOne - Draft Date One
 * @param draftDateTwo - Draft Date Two
 * @param frequency - Autopay Frequency
 * @param recurringPayment - Loan's RecurringPayment data
 *
 * @returns current and previous Autopay Confirm contexts
 */
export const getAutopayConfirmInput = ({
	amount,
	draftDateOne,
	draftDateTwo,
	frequency,
	recurringPayment,
}: {
	amount: number;
	draftDateOne: Date;
	draftDateTwo: Date | null;
	frequency: LoanFrequency;
	recurringPayment: LoanRecurringPayment;
}): {
	current: LoanAutopayConfirmInput;
	previous: LoanAutopayConfirmInput;
} => {
	const recurringPaymentDraftDateOne = UtilHelper.convertISOStringToDate(recurringPayment.draftDateOne);

	const recurringPaymentDraftDateTwo = recurringPayment.draftDateTwo
		? UtilHelper.convertISOStringToDate(recurringPayment.draftDateTwo)
		: new Date(recurringPaymentDraftDateOne.getDate() + 1);

	const currentDraftDateText = getAutopayPaymentFrequencyText({
		amount,
		draftDateOne,
		draftDateTwo,
		frequency,
	});

	const previousDraftDateText = getAutopayPaymentFrequencyText({
		amount: Number(recurringPayment.paymentTotalByFrequency),
		draftDateOne: recurringPaymentDraftDateOne,
		draftDateTwo: recurringPaymentDraftDateTwo,
		frequency: recurringPayment.frequency,
	});

	const currentFrequencyText = getAutopayPaymentFrequencyFullText({
		draftDate: draftDateOne,
		frequency,
	});

	const previousFrequencyText = getAutopayPaymentFrequencyFullText({
		draftDate: recurringPaymentDraftDateOne,
		frequency: recurringPayment.frequency,
	});

	const currentConfirmInput = {
		draftText: currentDraftDateText,
		frequencyText: currentFrequencyText,
	};

	const previousConfirmInput = {
		draftText: previousDraftDateText,
		frequencyText: previousFrequencyText,
	};

	return {
		current: currentConfirmInput,
		previous: previousConfirmInput,
	};
};

/**
 * Gets the Loan Autopay Draft Date Two ISO String by Frequency
 *
 * @param availableDates - Recurring Payment Available Dates
 * @param draftDateOneISOString - ISO String for Draft Date One
 * @param frequency - Autopay Frequency
 * @param recurringPayment - Loan's RecurringPayment data
 *
 * @returns Draft Date Two ISO String
 */
export const getAutopayDraftDateTwoISOString = ({
	availableDates,
	draftDateOneISOString,
	frequency,
	recurringPayment,
}: {
	availableDates: LoanRecurringPaymentAvailableDates;
	draftDateOneISOString: string;
	frequency: LoanFrequency;
	recurringPayment: LoanRecurringPayment | null;
}): string | null => {
	if (frequency !== LoanFrequency.SEMI_MONTHLY) {
		return null;
	}

	const draftDateOneDate = UtilHelper.convertISOStringToDate(draftDateOneISOString);
	const semiMonthlyAvailableDates = availableDates.semiMonthly;

	if (recurringPayment?.draftDateTwo) {
		const recurringPaymentDraftDateTwoDate = UtilHelper.convertISOStringToDate(recurringPayment.draftDateTwo);

		if (recurringPaymentDraftDateTwoDate <= draftDateOneDate) {
			const dayAfterDraftDateOne = moment(draftDateOneDate).add(1, 'day').format('YYYY-MM-DD');

			return dayAfterDraftDateOne;
		}

		return recurringPayment.draftDateTwo;
	}

	const dateOneIndex = semiMonthlyAvailableDates.findIndex((d) => d === draftDateOneISOString);

	if (dateOneIndex !== -1) {
		const dateTwoISOString = UtilHelper.addDaysToISOString(
			LOAN_AUTOPAY_DAYS_BETWEEN_SEMI_MONTHLY_DRAFT_DATES,
			draftDateOneISOString,
		);
		const dateTwoInAvailableDates = semiMonthlyAvailableDates.find((d) => d === dateTwoISOString);

		if (dateTwoInAvailableDates) {
			return dateTwoInAvailableDates;
		}

		const nextAvailableDate = semiMonthlyAvailableDates[dateOneIndex + 1];

		if (nextAvailableDate) {
			return nextAvailableDate;
		}
	}

	return moment(draftDateOneDate, 'YYYY-MM-DD').add(1, 'day').format('YYYY-MM-DD');
};

/**
 * Gets the Loan Autopay Draft Date ISO Strings by SemiMonthly Frequency
 *
 * @param availableDates - Recurring Payment Available Dates
 * @param recurringPayment - Loan's RecurringPayment data
 *
 * @returns draftDateOneISOString and draftDateTwoISOString
 */
export const getAutopayDraftDateISOStringsBySemiMonthly = ({
	availableDates,
	recurringPayment,
}: {
	availableDates: LoanRecurringPaymentAvailableDates;
	recurringPayment: LoanRecurringPayment | null;
}): {
	draftDateOneISOString: string;
	draftDateTwoISOString: string | null;
} => {
	const [draftDateOneISOString] = availableDates.semiMonthly;

	const draftDateTwoISOString = getAutopayDraftDateTwoISOString({
		availableDates,
		draftDateOneISOString,
		frequency: LoanFrequency.SEMI_MONTHLY,
		recurringPayment,
	});

	return {
		draftDateOneISOString,
		draftDateTwoISOString,
	};
};

/**
 * Gets the Loan Autopay Draft Date ISO Strings by Monthly Frequency
 *
 * @param availableDates - Recurring Payment Available Dates
 * @param daysDelinquent - number of days loan is delinquent
 * @param recurringPayment - Loan's RecurringPayment data
 *
 * @returns draftDateOneISOString and null for draftDateTwoISOString
 */
export const getAutopayDraftDateISOStringsByMonthly = ({
	availableDates,
	daysDelinquent,
	paymentDueDateISOString,
}: {
	availableDates: LoanRecurringPaymentAvailableDates;
	daysDelinquent: number;
	paymentDueDateISOString: string;
}): {
	draftDateOneISOString: string;
	draftDateTwoISOString: null;
} => {
	const today = new Date();
	const paymentDueDate = UtilHelper.convertISOStringToDate(paymentDueDateISOString);
	const isPaymentDueDateValid = paymentDueDate > today;
	const isDelinquent = daysDelinquent > 0;

	if (isDelinquent || !isPaymentDueDateValid) {
		const [draftDateOneISOString] = availableDates.monthly;

		return {
			draftDateOneISOString,
			draftDateTwoISOString: null,
		};
	}

	return {
		draftDateOneISOString: paymentDueDateISOString,
		draftDateTwoISOString: null,
	};
};

/**
 * Gets the Loan Autopay Draft Date ISO Strings by Weekly or SemiWeekly Frequency
 *
 * @param availableDates - Recurring Payment Available Dates
 *
 * @returns draftDateOneISOString and null for draftDateTwoISOString
 */
export const getAutopayDraftDateISOStringsByWeekly = ({
	availableDates,
}: {
	availableDates: LoanRecurringPaymentAvailableDates;
}): {
	draftDateOneISOString: string;
	draftDateTwoISOString: null;
} => {
	const [draftDateOneISOString] = availableDates.weekly;

	return {
		draftDateOneISOString,
		draftDateTwoISOString: null,
	};
};

/**
 * Gets the Loan Autopay Draft Date ISO Strings by Frequency
 *
 * @param availableDates - Recurring Payment Available Dates
 * @param daysDelinquent - number of days loan is delinquent
 * @param frequency - Autopay Frequency
 * @param paymentDueDateISOString - Next Payment Due Date as an ISO string
 * @param recurringPayment - Loan's RecurringPayment data
 *
 * @returns draftDateOneISOString and draftDateTwoISOString
 */
export const getAutopayDraftDateISOStringsByFrequency = ({
	availableDates,
	daysDelinquent,
	frequency,
	paymentDueDateISOString,
	recurringPayment,
}: {
	availableDates: LoanRecurringPaymentAvailableDates;
	daysDelinquent: number;
	frequency: LoanFrequency;
	paymentDueDateISOString: string;
	recurringPayment: LoanRecurringPayment | null;
}): {
	draftDateOneISOString: string;
	draftDateTwoISOString: string | null;
} => {
	if (frequency === LoanFrequency.MONTHLY) {
		return getAutopayDraftDateISOStringsByMonthly({
			availableDates,
			daysDelinquent,
			paymentDueDateISOString,
		});
	}

	if (frequency === LoanFrequency.SEMI_MONTHLY) {
		return getAutopayDraftDateISOStringsBySemiMonthly({
			availableDates,
			recurringPayment,
		});
	}

	return getAutopayDraftDateISOStringsByWeekly({ availableDates });
};

/**
 * Gets the Autopay Initial Draft Date One ISO string
 *
 * @param availableDates - Avaialble Dates Object from the API
 * @param frequency - Autopay Frequency
 * @param recurringPayment - Loan's Recurring Payment data
 *
 * @returns ISO string
 */
export const getAutopayInitialDraftDateOneISOString = ({
	availableDates,
	frequency,
	recurringPayment,
}: {
	availableDates: LoanRecurringPaymentAvailableDates;
	frequency: LoanFrequency;
	recurringPayment: LoanRecurringPayment | null;
}): string => {
	const today = new Date();
	const tomorrow = new Date(today.getDate() + 1);
	const tomorrowDateString = moment(tomorrow).format('YYYY-MM-DD');

	if (recurringPayment) {
		const draftDateOneDate = moment(recurringPayment.draftDateOne, DateFormat['YYYY-MM-DD']).toDate();
		const firstAvailableDate = availableDates[recurringPayment.frequency][0];

		if (draftDateOneDate <= tomorrow) {
			return firstAvailableDate ?? tomorrowDateString;
		}

		return recurringPayment.draftDateOne;
	}

	const firstAvailableDate = availableDates[frequency as LoanFrequency][0];

	return firstAvailableDate ?? tomorrowDateString;
};

/**
 * Given an array of LoanDetailsPeriod
 * Return the current period.
 *
 * Finds a period with a startDate equal to or after today's date
 * and an endDate before today's Date.
 *
 * If no period is found return null.
 *
 * @param periods
 * @returns - LoanDetailsPeriod or null
 */
export const getCurrentPeriod = (periods: LoanDetailsPeriod[]): LoanDetailsPeriod | null => {
	const today = new Date();
	const todayISOString = UtilHelper.convertDateToISOString(today);
	const todayMomentInstance = UtilHelper.convertISOStringToMomentInstance(todayISOString);

	const currentPeriod = periods.find((p) => {
		const startDateMomentInstance = UtilHelper.convertISOStringToMomentInstance(p.startDate);
		const endDateMomentInstance = UtilHelper.convertISOStringToMomentInstance(p.endDate);

		return (
			todayMomentInstance.isSameOrAfter(startDateMomentInstance)
			&& todayMomentInstance.isBefore(endDateMomentInstance)
		);
	});

	return currentPeriod ?? null;
};

/**
 * Given Partial<LoanESignModalState>,
 * return a default state object
 *
 * @param state
 * @returns - state
 */
export const getEsignModalDefaultState = (
	state?: Partial<LoanESignModalState>,
): LoanESignModalState => ({
	isShown: state?.isShown ?? false,
	isLoading: state?.isLoading ?? false,
	wasUpdateSuccessful: state?.wasUpdateSuccessful ?? false,
	wasUpdateTried: state?.wasUpdateTried ?? false,
});

/**
 * Returns the type select input options for Autopay Frequencies
 *
 * @param defaultOption - Optional FormSelectOption put at the beginning of the returned array
 *
 * @returns the array of type SelectOptions
 */
export const getFrequencySelectOptions = (
	defaultOption?: FormSelectOption,
): FormSelectOption[] => {
	const options: FormSelectOption[] = defaultOption ? [defaultOption] : [];

	LOAN_FREQUENCY_TEXT_MAP.forEach((text, frequency) => {
		options.push({
			text,
			value: frequency,
		});
	});

	return options;
};

/**
 * Given a frequency,
 * Return the text representation of that frequency from LOAN_FREQUENCY_MAP.
 *
 * If frequency is not found return an empty string.
 *
 * @param frequency
 */
export const getFrequencyText = (
	frequency: LoanFrequency,
): string => LOAN_FREQUENCY_MAP.get(frequency) ?? '';

/**
 * Returns the type select input options for an array of LoanLinkedAccounts
 *
 * @param accounts - array of LoanLinkedAccount
 * @param defaultOption - Optional FormSelectOption put at the beginning of the returned array
 *
 * @returns the array of type SelectOptions
 */
export const getLinkedAccountsSelectOtions = (
	accounts: LoanLinkedAccount[],
	defaultOption?: FormSelectOption,
): FormSelectOption[] => {
	const options = accounts.map((account) => ({
		value: account.id,
		text: account.displayName,
	}));

	return defaultOption
		? [defaultOption, ...options]
		: options;
};

/**
 * Get the href string for a Loan Statements Download link
 *
 * @param loanId - string loan id
 *
 * @returns href view string
 */
export const getStatementDownloadLink = (
	loanId: string,
	statement: LoanStatement,
): string => `${LOAN_API_PREFIX}/loan/${loanId}/statements/download/${statement.name}`;

/**
 * Get the href string for a Loan Statements View link
 *
 * @param loanId - string loan id
 *
 * @returns href view string
 */
export const getStatementViewLink = (
	loanId: string,
	statement: LoanStatement,
): string => `${LOAN_API_PREFIX}/loan/${loanId}/statements/view/${statement.name}`;

/**
 * Returns an array of unique years from an array of LoanStatements
 *
 * @param statements - array of LoanStatements
 *
 * @returns an array of strings of unique years
 */
export const getStatementsUniqueYears = (
	statements: LoanStatement[],
): string[] => {
	let year: string;

	const statementsUniqueByYear = uniqBy(statements, (statement: LoanStatement) => {
		[year] = statement.date.split('-');
		return year;
	});

	const years = statementsUniqueByYear.map((statement) => {
		[year] = statement.date.split('-');
		return year;
	});

	return years;
};

/**
 * Returns the select input options available for Statements
 *
 * @remarks
 *
 * Given an array of LoanStatements
 * if there are only statements available in the current year,
 * then return only the current year as an option
 * else return the array of unqiue years prepended with the default option.
 *
 * @param statements - array of LoanStatements
 * @param defaultOption - string representing the default option to prepend to the array of years returned
 *
 * @returns the array strings representing the select options
 */
export const getStatementsSelectOptions = (
	statements: LoanStatement[],
	defaultOption: string,
): string[] => {
	const years = getStatementsUniqueYears(statements);

	return years.length > 1
		? [defaultOption, ...years]
		: years;
};

/**
 * Given a transaction
 * If transaction is a TakeBack
 * Return the transaction amount as a formatted dollar amount in the form of ($amount)
 *
 * If transaction is not a TakeBack
 * Return the transaction amount as a formatted dollar amount in the form of $amount
 *
 * @param transaction
 * @returns - string of Transaction Amount Text
 */
export const getTransactionAmountText = (transaction: LoanTransaction): string => {
	if (!transaction) {
		return '';
	}

	const isTakeBack = checkIsTransactionOfType({
		transaction,
		type: LoanTransactionTypeId.TAKE_BACK,
	});

	const formattedAsDollar = UtilHelper.formatDollar(transaction.amount);

	const amountText = isTakeBack
		? UtilHelper.prependAppend({
			append: ')',
			original: formattedAsDollar,
			prepend: '(',
		})
		: formattedAsDollar;

	return amountText;
};

/**
 * Gets the year from a Transactions effectDate
 *
 * @param transaction - LoanTransaction
 *
 * @returns year
 */
export const getTransactionYear = (transaction: LoanTransaction): string => {
	const [year] = transaction.effectiveDate.split('-');

	return year;
};

/**
 * Returns the description select input options for an array of LoanTransactions
 *
 * @param transactions - array of LoanTransactions
 * @param defaultOption - FormSelectOption put at the beginning of the returned array
 *
 * @returns the array of description SelectOptions
 */
export const getTransactionsDescriptionSelectOptions = (
	transactions: LoanTransaction[],
	defaultOption?: FormSelectOption,
): FormSelectOption[] => {
	const uniqueTransactions = uniqBy(transactions, (transaction) => transaction.description);
	const options: FormSelectOption[] = defaultOption ? [defaultOption] : [];

	uniqueTransactions.forEach((transaction) => {
		options.push({
			value: transaction.description,
			text: transaction.description,
		});
	});

	return options;
};

/**
 * Returns the status select input options for an array of LoanTransactions
 *
 * @param transactions - array of LoanTransactions
 * @param defaultOption - Optional FormSelectOption put at the beginning of the returned array
 *
 * @returns the array of status SelectOptions
 */
export const getTransactionsStatusSelectOptions = (
	transactions: LoanTransaction[],
	defaultOption?: FormSelectOption,
): FormSelectOption[] => {
	const uniqueTransactions = uniqBy(transactions, (transaction) => transaction.status);
	const options: FormSelectOption[] = defaultOption ? [defaultOption] : [];

	uniqueTransactions.forEach((transaction) => {
		options.push({
			value: String(transaction.status),
			text: LOAN_TRANSACTION_STATUS_MAP.get(transaction.status) || '',
		});
	});

	return options;
};

/**
 * Returns the type select input options for an array of LoanTransactions
 *
 * @param transactions - array of LoanTransactions
 * @param defaultOption - Optional FormSelectOption put at the beginning of the returned array
 *
 * @returns the array of type SelectOptions
 */
export const getTransactionsTypeSelectOptions = (
	transactions: LoanTransaction[],
	defaultOption?: FormSelectOption,
): FormSelectOption[] => {
	const uniqueTransactions = uniqBy(transactions, (transaction) => transaction.transactionType);
	const options: FormSelectOption[] = defaultOption ? [defaultOption] : [];

	uniqueTransactions.forEach((transaction) => {
		options.push({
			value: String(transaction.transactionType),
			text: LOAN_TRANSACTION_TYPE_MAP.get(transaction.transactionType) || '',
		});
	});

	return options;
};

/**
 * Returns the year select input options for an array of LoanTransactions
 *
 * @param transactions - array of LoanTransactions
 * @param defaultOption - Optional FormSelectOption put at the beginning of the returned array
 *
 * @returns the array of year SelectOptions
 */
export const getTransactionsYearSelectOptions = (
	transactions: LoanTransaction[],
	defaultOption?: FormSelectOption,
): FormSelectOption[] => {
	const uniqueTransactions = uniqBy(
		transactions,
		(transaction) => getTransactionYear(transaction),
	);
	const options: FormSelectOption[] = defaultOption ? [defaultOption] : [];

	uniqueTransactions.forEach((transaction) => {
		const year = getTransactionYear(transaction);

		options.push({
			value: year,
			text: year,
		});
	});

	return options;
};

/**
 * Gets the Transaction Detail from a Detail id
 *
 * @param id - Transaction Detail id
 *
 * @returns Transaction Detail
 */
export const resolveTransactionDetailId = (
	id: LoanTransactionDetailId,
): string => LOAN_TRANSACTION_DETAIL_MAP.get(id) || '';

/**
 * Gets the Transaction Status from a Status id
 *
 * @param id - Transaction Status id
 *
 * @returns Transaction Status
 */
export const resolveTransactionStatusId = (
	id: LoanTransactionStatusId,
): string => LOAN_TRANSACTION_STATUS_MAP.get(id) || '';

/**
 * Gets the Transaction Type from a Type id
 *
 * @param id - Transaction Type id
 *
 * @returns Transaction Type
 */
export const resolveTransactionTypeId = (
	id: LoanTransactionTypeId,
): string => LOAN_TRANSACTION_TYPE_MAP.get(id) || '';

/**
 * Logic function for determining the payment status of a loan
 *
 * @param isAutopayOn
 * @param loanDetails
 *
 * @returns PaymentStatus
 */
export const getPaymentStatus = (loanDetails: LoanDetails, isAutopayOn?: boolean): LoanPaymentStatus => {
	const {
		delinquentDays,
		installmentAmount,
		remainingInstallmentDue,
	} = loanDetails.general;

	if (Number(remainingInstallmentDue) === 0) {
		return LoanPaymentStatus.FULLY_PAID;
	}

	if (Number(delinquentDays) > 0) {
		return LoanPaymentStatus.OVERDUE;
	}

	if (!isAutopayOn && remainingInstallmentDue === installmentAmount) {
		return LoanPaymentStatus.NOT_YET_PAID;
	}

	if (!isAutopayOn && remainingInstallmentDue < installmentAmount) {
		return LoanPaymentStatus.PARTIALLY_PAID;
	}

	if (isAutopayOn) {
		return LoanPaymentStatus.SCHEDULED;
	}

	return LoanPaymentStatus.CLOSED;
};
